import Skeleton from '@carvana/ds-skeleton';
import cx from 'classnames';
import LiveAnnouncer from '@/common/LiveAnnouncer';

type OwnProps = {
  isLite?: boolean;
};

export default function VehicleSkeletonLayout({ isLite = false }: OwnProps) {
  return (
    <main className="bg-background-default">
      <LiveAnnouncer message="Loading vehicle details" />
      <div className="mx-auto max-w-[1440px] px-0 md:px-3xl lg:px-[clamp(var(--spacing-4xl),calc((100vw-1440px)/2+84px),84px)]">
        {/* Breadcrumbs */}
        <div className="flex items-center justify-between px-2xl py-lg md:px-0">
          <div className="flex items-center gap-sm">
            <Skeleton className="!rounded-sm" height="14px" width="52px" />
            <Skeleton className="!rounded-sm" height="14px" width="8px" />
            <Skeleton className="!rounded-sm" height="14px" width="36px" />
            <Skeleton className="!rounded-sm" height="14px" width="8px" />
            <Skeleton className="!rounded-sm" height="14px" width="120px" />
          </div>
          <div className="flex gap-lg">
            <Skeleton className="!rounded-full" height="24px" width="24px" />
            <Skeleton className="!rounded-full" height="24px" width="24px" />
          </div>
        </div>

        {/* Grid — matches real VDP: grid-cols-1 → sm:grid-cols-2 → md:grid-cols-12 (8 content / 4 rail) */}
        <div className="relative grid grid-cols-1 gap-x-2xl bg-background-default sm:grid-cols-2 md:grid-cols-12">
          {/* Row 1: Hero image + thumbnail strip — full-width at tablet, 8 cols at desktop */}
          <div className="sm:col-span-2 md:col-span-8">
            <Skeleton className="aspect-video !h-auto !w-full !rounded-none md:!rounded-xl" />
            {/* Thumbnails hidden on mobile — real Hero hides the Gallery component on mobile */}
            <div className="hidden gap-xl px-2xl pt-xl pb-4xl sm:flex md:px-0">
              <Skeleton className="aspect-[4/3] !h-auto !rounded-md flex-1" />
              <Skeleton className="aspect-[4/3] !h-auto !rounded-md flex-1" />
              <Skeleton className="aspect-[4/3] !h-auto !rounded-md flex-1" />
              <Skeleton className="aspect-[4/3] !h-auto !rounded-md flex-1" />
            </div>
          </div>

          {/* Col 2, rows 1–3: Sidebar
              Standard (isLite=false): sm:row-start-3 md:row-start-1
                → at tablet sidebar drops below hero; at desktop it sits beside it.
              Lite (isLite=true): sm:row-start-1
                → sidebar beside hero from tablet up. */}
          <div
            className={cx(
              'z-10 sm:col-start-2 sm:row-span-3 sm:mt-3xl sm:pb-5xl sm:pr-2xl md:col-start-9 md:col-span-4 md:mt-0 md:pb-7xl md:pr-0 min-[1440px]:pl-xl',
              isLite ? 'sm:row-start-1' : 'sm:row-start-3 md:row-start-1'
            )}
          >
            <div className="sticky top-[var(--sticky-info-card-top-sm,32px)] flex flex-col gap-3xl px-2xl py-3xl sm:rounded-xl sm:px-2xl sm:shadow-[0px_28px_56px_0px_rgba(36,41,46,0.04),_0px_8px_16px_0px_rgba(36,41,46,0.12)] md:top-[var(--sticky-info-card-top-md,32px)] lg:p-4xl">
              {/* Badge */}
              <Skeleton className="!rounded-full" height="24px" width="88px" />
              {/* Vehicle title */}
              <div className="flex flex-col gap-sm">
                <Skeleton className="!w-full !rounded-sm" height="34px" />
                <Skeleton className="!rounded-sm" height="34px" width="75%" />
                <Skeleton className="!rounded-sm" height="18px" width="56%" />
              </div>
              {/* Price block */}
              <div className="flex flex-col gap-sm">
                <div className="flex items-baseline gap-md">
                  <Skeleton
                    className="!rounded-sm"
                    height="32px"
                    width="130px"
                  />
                  <Skeleton
                    className="!rounded-sm"
                    height="18px"
                    width="100px"
                  />
                </div>
                {/* Incentives row */}
                <Skeleton className="!w-full !rounded-lg" height="40px" />
                <Skeleton className="!rounded-sm" height="18px" width="180px" />
                <Skeleton className="!rounded-sm" height="16px" width="120px" />
                <Skeleton className="!rounded-sm" height="18px" width="200px" />
              </div>
              {/* CTA */}
              <Skeleton className="!h-[52px] !w-full !rounded-full" />
            </div>
          </div>

          {/* Row 2: DesktopPageNavigation (nav tabs) — hidden on mobile, full-width at tablet, 8 cols at desktop */}
          <div className="hidden items-center gap-4xl border-b border-border-weak px-2xl pt-2xl pb-lg sm:row-start-2 sm:col-span-2 sm:flex md:col-span-8 md:px-0">
            <Skeleton className="!rounded-sm" height="20px" width="68px" />
            <Skeleton className="!rounded-sm" height="20px" width="60px" />
            <Skeleton className="!rounded-sm" height="20px" width="96px" />
            <Skeleton className="!rounded-sm" height="20px" width="52px" />
            <Skeleton className="!rounded-sm" height="20px" width="76px" />
          </div>

          {/* Row 3, Cols 1–8: MainContentWrapper */}
          <div className="row-start-3 flex flex-col gap-4xl px-2xl py-4xl sm:pl-2xl md:col-span-8 md:pl-0">
            <div className="flex flex-col gap-md">
              <Skeleton className="!rounded-sm" height="20px" width="140px" />
              <Skeleton className="!h-[140px] !w-full !rounded-lg" />
            </div>
            {!isLite && (
              <div className="flex flex-col gap-md">
                <Skeleton className="!rounded-sm" height="20px" width="160px" />
                <Skeleton className="!h-[100px] !w-full !rounded-lg" />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
